import { useEffect, useState } from "react";
import { UserCog, Trash2, KeyRound, ShieldCheck, LifeBuoy } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

export default function SuperTeam() {
  const { user } = useAuth();
  const [team, setTeam] = useState([]);
  const [form, setForm] = useState({ email: "", password: "", level: "support" });
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [confirmDelete, setConfirmDelete] = useState("");

  const load = () => api.get("/admin/team").then((r) => setTeam(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (form.password.length < 8) return toast.error("Password needs at least 8 characters");
    try {
      await api.post("/admin/team", form);
      toast.success(`${form.email} added to the team`);
      setForm({ email: "", password: "", level: "support" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (m) => {
    if (confirmDelete !== m.id) { setConfirmDelete(m.id); return; }
    try {
      await api.delete(`/admin/team/${m.id}`);
      toast.success(`${m.email} removed`);
      setConfirmDelete("");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const changePw = async () => {
    if (pw.new_password.length < 8) return toast.error("New password needs at least 8 characters");
    try {
      await api.post("/admin/password", pw);
      toast.success("Your password has been changed");
      setPw({ current_password: "", new_password: "" });
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="super-team-page" className="max-w-3xl">
      <span className="nb-label">Team</span>
      <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-2">Staff accounts</h1>
      <p className="text-zinc-500 mt-2 max-w-xl">
        <span className="font-bold">Full admins</span> can do everything, including platform settings.
        <span className="font-bold"> Support staff</span> get read-only access to schools, parents and vouchers, and can comp accounts — nothing else.
      </p>

      <div className="mt-8 nb-card overflow-hidden" data-testid="team-list">
        {team.map((m) => (
          <div key={m.id} className="flex items-center justify-between gap-3 p-4 border-b-2 border-zinc-100 last:border-b-0" data-testid={`team-row-${m.id}`}>
            <div className="flex items-center gap-3 min-w-0">
              <div className={`w-9 h-9 shrink-0 rounded-xl border-2 border-zinc-900 flex items-center justify-center ${m.level === "full" ? "bg-[#A7E4D3]" : "bg-[#FDE68A]"}`}>
                {m.level === "full" ? <ShieldCheck className="w-4 h-4" /> : <LifeBuoy className="w-4 h-4" />}
              </div>
              <div className="min-w-0">
                <div className="font-bold text-sm truncate">{m.email}</div>
                <div className="text-xs text-zinc-500 font-semibold">{m.level === "full" ? "Full admin" : "Support (read-only + comp)"}{user && user.id === m.id ? " · you" : ""}</div>
              </div>
            </div>
            {user && user.id !== m.id && (
              <button onClick={() => remove(m)} data-testid={`team-delete-${m.id}`}
                className="nb-btn bg-red-100 text-red-700 px-3 py-1.5 text-xs shrink-0">
                <Trash2 className="w-3.5 h-3.5" /> {confirmDelete === m.id ? "Confirm?" : "Remove"}
              </button>
            )}
          </div>
        ))}
      </div>

      <div className="mt-6 nb-card p-6" data-testid="team-add-card">
        <h2 className="font-display font-bold text-lg flex items-center gap-2"><UserCog className="w-5 h-5" /> Add a team member</h2>
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <input className="nb-input" placeholder="Email" type="email" data-testid="team-add-email"
            value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <input className="nb-input" placeholder="Password (min 8 chars)" data-testid="team-add-password"
            value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <select className="nb-input" data-testid="team-add-level" value={form.level}
            onChange={(e) => setForm({ ...form, level: e.target.value })}>
            <option value="support">Support — read-only + comp</option>
            <option value="full">Full admin</option>
          </select>
          <button onClick={add} data-testid="team-add-submit" className="nb-btn bg-[#A7E4D3] px-5 py-2.5 text-sm">Add to team</button>
        </div>
      </div>

      <div className="mt-6 nb-card p-6" data-testid="team-password-card">
        <h2 className="font-display font-bold text-lg flex items-center gap-2"><KeyRound className="w-5 h-5" /> Change my password</h2>
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <input className="nb-input" type="password" placeholder="Current password" data-testid="team-pw-current"
            value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} />
          <input className="nb-input" type="password" placeholder="New password (min 8 chars)" data-testid="team-pw-new"
            value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} />
        </div>
        <button onClick={changePw} data-testid="team-pw-submit" className="nb-btn bg-[#FDE68A] px-5 py-2.5 text-sm mt-3">Change password</button>
      </div>
    </div>
  );
}
